import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import heroImage from "@/assets/hero-archivist.jpg?format=webp&quality=72";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";
import { getCurrentChapter, isPurchasable } from "@/config/chapters";


export function Hero() {
  const imgRef = useRef<HTMLImageElement>(null);

  // Mobile-only subtle parallax: bg image moves at 40% of scroll, capped 80px.
  // Disabled on >=768px and when the user prefers reduced motion.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqDesktop = window.matchMedia("(min-width: 768px)");
    const mqReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mqDesktop.matches || mqReduced.matches) return;

    const el = imgRef.current;
    if (!el) return;
    el.classList.add("hero-parallax");

    let raf = 0;
    const update = () => {
      raf = 0;
      const y = Math.min(window.scrollY * 0.4, 80);
      el.style.transform = `translate3d(0, ${y}px, 0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      el.style.transform = "";
    };
  }, []);

  return (
    <section
      id="top"
      className="hero-ambient-fade relative grain flex min-h-[88vh] items-center overflow-hidden sm:min-h-screen"
    >
      <ResponsiveImage
        ref={imgRef}
        src={heroImage}
        alt="The Archivist — a black House of Apocrypha natural perfume bottle with an ornate gold floral label and a polished wood cap, on marble with cream silk, a quill, an aged manuscript and a peacock feather"
        width={1380}
        height={780}
        priority
        sizes={SIZES.hero}
        widths={[640, 960, 1280, 1600, 1920]}
        pictureClassName="contents"
        className="absolute inset-0 h-full w-full object-cover object-[68%_center] will-change-transform"
      />

      {/* Left-to-right darkening so the copy reads */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, oklch(0.13 0.01 60 / 0.94) 0%, oklch(0.13 0.01 60 / 0.7) 38%, oklch(0.13 0.01 60 / 0.15) 70%, oklch(0.13 0.01 60 / 0.4) 100%)",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-40"
        style={{ background: "var(--gradient-veil)" }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-20">
        <div className="max-w-xl">
          <p className="eyebrow">A Natural Perfume Studio</p>

          <h1 className="mt-5 font-[var(--font-logo)] text-4xl leading-[1.08] tracking-wide text-parchment sm:text-5xl lg:text-[3.4rem]">
            <span className="block">House of Apocrypha</span>
            <span className="mt-4 block font-serif text-lg leading-relaxed text-gold/90 sm:text-xl normal-case tracking-normal">
              Natural perfumes of story, ritual &amp; alchemy
            </span>
          </h1>

          <p className="mt-5 max-w-lg font-serif text-lg leading-relaxed text-parchment/85 sm:text-xl">
            Each fragrance is blended slowly by hand and released in seasonal
            chapters — finished in small batches, never repeated.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            {isPurchasable(getCurrentChapter().state) ? (
              <Link
                to="/products"
                className="group inline-flex items-center justify-center gap-3 border border-gold bg-gold px-8 py-4 font-display text-xs uppercase tracking-[0.26em] text-primary-foreground transition-all duration-300 ease-out hover:bg-gold-soft hover:shadow-[0_0_20px_rgba(184,164,123,0.25)] active:scale-[0.97]"
              >
                Browse Open Chapters
                <span className="transition-transform duration-500 group-hover:translate-x-1">
                  →
                </span>
              </Link>
            ) : (
              <Link
                to="/products"
                className="group inline-flex items-center justify-center gap-3 border border-gold bg-gold px-8 py-4 font-display text-xs uppercase tracking-[0.26em] text-primary-foreground transition-all duration-300 ease-out hover:bg-gold-soft hover:shadow-[0_0_20px_rgba(184,164,123,0.25)] active:scale-[0.97]"
              >
                Read the Current Chapter
                <span className="transition-transform duration-500 group-hover:translate-x-1">
                  →
                </span>
              </Link>
            )}

            <a
              href="#release-access"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("release-access")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center justify-center gap-3 border border-gold/60 bg-background/20 px-8 py-4 font-display text-xs uppercase tracking-[0.26em] text-parchment backdrop-blur-sm transition-colors duration-500 hover:border-gold hover:bg-gold/10"
            >
              Join the Release List
              <span className="transition-transform duration-500 group-hover:translate-x-1">
                →
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
